import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import type { ConversionTrackingView, VenueCampaignPerformanceItem } from '@/lib/venue/campaignPerformanceTypes'
import {
  chargeStatusDisplayText,
  conversionStatusText,
  regionDisplayText,
} from '@/lib/venue/campaignPerformanceViewModel'
import { readRateText } from '@/lib/venue/campaignViewModel'
import { formatAmount, formatDateTime } from '@/lib/venue/format'

/**
 * 성과 목록 표(요구 3, #734 §1-3) — 발송일시·제목·대상지역·발송건수·열람(수/율)·전환(건수/율)·
 * 예상/실제 비용·과금상태 + "더 보기" 페이지네이션. `NoticeCampaignListTable`과 같은 마크업 패턴.
 */
export function PromotionPerformanceListTable({
  items,
  conversionTracking,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  items: VenueCampaignPerformanceItem[]
  conversionTracking: ConversionTrackingView
  onSelect: (item: VenueCampaignPerformanceItem) => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  if (items.length === 0) {
    return <EmptyState title="조회된 홍보 성과가 없습니다" />
  }

  const conversionNotice = conversionStatusText(conversionTracking)

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-slateish-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slateish-200 bg-slateish-50 text-left text-xs font-medium text-slateish-600">
              <th className="px-3 py-2">발송일시</th>
              <th className="px-3 py-2">제목</th>
              <th className="px-3 py-2">대상 지역</th>
              <th className="px-3 py-2">발송 건수</th>
              <th className="px-3 py-2">열람(수/율)</th>
              <th className="px-3 py-2">전환(건수/율)</th>
              <th className="px-3 py-2">예상 비용</th>
              <th className="px-3 py-2">실제 비용</th>
              <th className="px-3 py-2">과금 상태</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.campaignId}
                role="button"
                tabIndex={0}
                onClick={() => {
                  onSelect(item)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onSelect(item)
                }}
                className="cursor-pointer border-b border-slateish-200 last:border-b-0 hover:bg-brand-50/40"
              >
                <td className="px-3 py-2">{formatDateTime(item.sentAt)}</td>
                <td className="px-3 py-2">{item.title}</td>
                <td className="px-3 py-2">{regionDisplayText(item)}</td>
                <td className="px-3 py-2">{item.successCount.toLocaleString('ko-KR')}</td>
                <td className="px-3 py-2">
                  {item.readCount.toLocaleString('ko-KR')} / {readRateText(item.readRatePercent)}
                </td>
                <td className="px-3 py-2">
                  {conversionNotice ??
                    `${item.convertedCount.toLocaleString('ko-KR')} / ${readRateText(item.conversionRatePercent)}`}
                </td>
                <td className="px-3 py-2">{formatAmount(item.estimatedAmount)}</td>
                <td className="px-3 py-2">
                  {item.chargedAmount === null ? '확정 대기' : formatAmount(item.chargedAmount)}
                </td>
                <td className="px-3 py-2">{chargeStatusDisplayText(item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={onLoadMore}>
            {isFetchingNextPage ? '불러오는 중' : '더 보기'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
